import { Injectable, OnDestroy } from "@angular/core";
import { Router, RouterStateSnapshot } from "@angular/router";
import { StorageService } from "../";
import { UserEndPoint, AirAppRoutes } from "../../lib/air-api-constant";
import { HttpClient } from "@angular/common/http";
import { FilterService } from "../filter/filter.service";
import { BehaviorSubject, Subject } from 'rxjs';
import { environment } from "../../../../../../environments/environment";
import { UserSetting } from "../../lib/types/general";
import { MessageService } from "primeng/api";
import { DialogService } from "../dialog/dialog.service";
import { CommonService } from "../common/common.service";

export interface UserDetails {
  id: string,
  firstName?: string,
  lastName?: any,
  email?: string,
  role: string,
  userName: string,
}

export enum UserTypes {
  USER = 'user',
  MANAGER = 'manager',
  CUSTOMER = 'customer',
}

@Injectable({
  providedIn: "root",
})

export class AuthService implements OnDestroy {

  public static lastApiCall: any = null;
  public static readonly CROSS_APP_LOGOUT_KEY = 'cross_app_logout';
  private readonly schedule: any
  private user!: UserDetails;
  private readonly userPromise = new BehaviorSubject<UserDetails>(this.user);
  public $user = this.userPromise.asObservable();
  private readonly eluaPromise = new BehaviorSubject<any>(null);
  public $eula = this.eluaPromise.asObservable();
  private readonly companyListPromise = new BehaviorSubject<any>(null);
  public $companyList = this.companyListPromise.asObservable();
  data: any = null;

  private readonly pendingHTTPRequests$ = new Subject<void>();
  private listenerAdded = false; // Flag to track if listener has been added
  private readonly navigatorOrigin = this.getNavigatorOrigin();
  private isNavigatorLogoutSignal(payload: any): boolean {
    if (payload === 'LOGOUT') {
      return true;
    }

    if (!payload || typeof payload !== 'object') {
      return false;
    }

    return payload.type === 'LOGOUT';
  }
  private notifyNavigatorLogout(): void {
    const logoutPayload = { type: 'LOGOUT', source: 'AIR' };

    try {
      if (globalThis.opener && !globalThis.opener.closed) {
        globalThis.opener.postMessage(logoutPayload, this.navigatorOrigin);
      }
    } catch {
      // Cross-window communication can fail if opener is not accessible.
    }

    try {
      if (globalThis.parent && globalThis.parent !== globalThis.window) {
        globalThis.parent.postMessage(logoutPayload, this.navigatorOrigin);
      }
    } catch {
      // Cross-window communication can fail if parent is not accessible.
    }
  }
  private readonly crossWindowMessageListener = (event: MessageEvent) => {
    if (event.origin !== this.navigatorOrigin) {
      return;
    }

    const payload = event.data;
    if (payload && typeof payload === 'object' && payload.type === 'JWT_TOKEN' && typeof payload.token === 'string' && payload.token.trim()) {
      const jwt = payload.token;
      this.storageService.setItem('token', jwt);
      this.decodeAndStoreTokenInfo(jwt);
      this.loginRedirect();
      return;
    }

    if (this.isNavigatorLogoutSignal(payload)) {
      this.logoutRedirect(false);
    }
  };
  private readonly crossAppLogoutListener = (event: StorageEvent) => {
    if (event.key === AuthService.CROSS_APP_LOGOUT_KEY) {
      this.logoutRedirect(false);
    }
  };

  constructor(
    public router: Router,
    protected http: HttpClient,
    private readonly commonService: CommonService,
    public storageService: StorageService,
    private readonly filterService: FilterService,
    public messageService: MessageService,
    private readonly dialogService: DialogService,

  ) {
    this.updateUser(this.storageService.getItem('user'));
    this.setupLogoutListener();
    this.crossLogin();
  }

  private getNavigatorOrigin(): string {
    try {
      return new URL(environment.navigatorloginUrl).origin;
    } catch {
      return window.location.origin;
    }
  }

  crossLogin() {
    const navOrigin = this.navigatorOrigin;

    // Add listener only once (if it hasn't been added yet)
    if (!this.listenerAdded) {
      window.addEventListener('message', this.crossWindowMessageListener);

      // Set the flag to true to prevent adding the listener again
      this.listenerAdded = true;
    }

    // Check if token already exists
    const existingToken = this.storageService.getItem('token');
    if (existingToken) {
    //  console.log('Token already exists, decoding stored token');
      // Decode and process the token info
      this.decodeAndStoreTokenInfo(existingToken);
      // Optionally, redirect after processing the token
      //  this.loginRedirect();
      return; // Token exists, no need to add listener
    }
    // Check if token exists and is valid

    // If no token exists, send the request to the parent window for the JWT token
    window.opener?.postMessage('REQUEST_JWT', navOrigin);
  }

  private setupLogoutListener() {
    window.addEventListener('storage', this.crossAppLogoutListener);
  }

  clearTokenOnClose() {
    const listeners = {
        beforeunload: () => {
            // Check if current URL is external tracking
            const isExternalTracking = window.location.href.includes('external-tracking-list/details');
            
            if (!isExternalTracking) {
                // Only clear storage if not external tracking
                localStorage.clear();
                sessionStorage.clear();

                // Remove specific tokens
                this.storageService.removeItem('token');
                this.storageService.removeItem('user');
                this.storageService.removeItem('refreshToken');

                // Clear intervals and listeners
                clearInterval(this.schedule);
                window.removeEventListener('message', this.crossWindowMessageListener);

                // Notify other tabs
                localStorage.setItem(AuthService.CROSS_APP_LOGOUT_KEY, Date.now().toString());

                // Complete observables
               this.pendingHTTPRequests$.complete();
               this.userPromise.complete();

                // Reset flags
                this.listenerAdded = false;
            }
        },

        visibilitychange: () => {
            if (document.visibilityState === 'hidden') {
                const isExternalTracking = window.location.href.includes('external-tracking-list/details');
                if (!isExternalTracking) {
                    this.messageService.clear();
                    this.dialogService.updateDialogData("");
                }
            }
        }
    };

    // Add listeners
    window.addEventListener('beforeunload', listeners.beforeunload);
    document.addEventListener('visibilitychange', listeners.visibilitychange);

    // Store for cleanup
    this.cleanupListeners = () => {
        window.removeEventListener('beforeunload', listeners.beforeunload);
        document.removeEventListener('visibilitychange', listeners.visibilitychange);
    };
}

 // Add to class definition:
 
  private cleanupListeners!: () => void;

  // Add to ngOnDestroy 
  ngOnDestroy() {
    window.removeEventListener('message', this.crossWindowMessageListener);
    window.removeEventListener('storage', this.crossAppLogoutListener);
    this.listenerAdded = false;
    if (this.cleanupListeners) {
      this.cleanupListeners();
    }
  }

  private decodeAndStoreTokenInfo(token: string) {
    try {
      // Decode JWT payload
      const tokenParts = token.split('.');
      const base64 = tokenParts[1].replace(/-/g, '+').replace(/_/g, '/');
      const padding = '='.repeat((4 - base64.length % 4) % 4);
      const decodedPayload = JSON.parse(atob(base64 + padding));
      //for ump 19 april------------ kalindi 
      const firstName = decodedPayload.given_name || '';
      const lastName = decodedPayload.family_name || '';
      const userName = `${firstName} ${lastName}`.trim();
      //const userName = decodedPayload.name || '';
      // Store user info from token
      this.storageService.setItem('user', userName);
      this.storageService.setItem('email', decodedPayload.name || '');


      // Update user settings
     // UserSetting.userName = fullName;
     UserSetting.userName = userName;
      UserSetting.userBDPEmail = decodedPayload.name;
      UserSetting.companyName = decodedPayload['custom:company_name'];

      return decodedPayload;
    } catch (error) {
      console.error('Error decoding token:', error);
      return null;
    }
  }
  //#region PUBLIC METHODS
  public get token() {
    return this.storageService.getItem("token");//Get the token from storage
  }

  public setToken(token: string) {
    this.storageService.setItem("token", token);//Set the token while loggin for first time
  }

  public checkAuth(state: RouterStateSnapshot) {
    return true;                                //Check user state
  }

  public loginRedirect() {
    let user = "customer";//to be removed later
    switch (user) {
      case "manager": {
        this.commonService.navigateRoute({
          route: AirAppRoutes.Manager.USER_CONTROL,
          type: 'Manager',
        });
        break;
      }
      case "customer": {
        this.commonService.navigateRoute({
          route: AirAppRoutes.User.HOME,
          type: 'User',
        });
        break;
      }
      default:
        this.commonService.navigateRoute({
          route: AirAppRoutes.User.HOME,
          type: 'User',
        });
    }
  }

  /**
   * Redirect to Login after logout
   */
  public logoutRedirect(shouldBroadcast = true) {
    localStorage.clear();
    sessionStorage.clear();

    // Remove auth tokens if any
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    localStorage.removeItem('refreshToken');

    if (shouldBroadcast) {
      localStorage.setItem(AuthService.CROSS_APP_LOGOUT_KEY, Date.now().toString());
      this.notifyNavigatorLogout();
    }

    window.location.href = environment.navigatorlogoutUrl;
    window.addEventListener('beforeunload', () => {
      localStorage.clear();
      sessionStorage.clear();
    });

  }
  /**
   * Password confirmation for the user
   */
  public passwordResetConfirmNav() {
    this.commonService.navigateRoute({
      route: AirAppRoutes.Auth.PASSWORD_CONFIRM,
    });
  }

  /**
   * Resend link for the user
   */
  public passwordResendNav() {
    this.commonService.navigateRoute({
      route: AirAppRoutes.Auth.RESEND_LINK,
    });
  }
  //#endregion

  //#region PUBLIC METHODS FOR API CALL FOR AUTHENTICATION
  //Logout method removing the email, passowrd, token, keepsignin and state
  public logout() {
    clearInterval(this.schedule);
    UserSetting.reset();
    const rememberEmail = this.storageService.getItem('rememberEmail');
    let email = this.storageService.getItem('email');
    const recentShipment = this.storageService.getItem('recentShipment-' + email);
    const redirectUrl = this.storageService.getItem('redrectUrl');
    localStorage.clear();
    //Do not clear below fields rememberEmail,email,recentShipment we need it after login.
    this.storageService.setItem('rememberEmail', rememberEmail);
    this.storageService.setItem('email', email);
    this.storageService.setItem('recentShipment-' + email, recentShipment);
    this.storageService.setItem('redirectUrl', redirectUrl);
    this.filterService.filterSelectionDownloadOption.next("");
    this.filterService.updateFilterSelection('noFilter', "Logout");
    this.logoutRedirect();
  }

  public getUser() {
    return this.user;
  }

  public resetPasswordAuth(body: any) {
    return this.http.get(
      UserEndPoint.AuthPoints.RESET_PASSWORD,
      body
    );
  }

  public updateUser(userDetails: UserDetails) {
    this.user = userDetails;
    this.storageService.setItem('user', userDetails);
    this.userPromise.next(userDetails);
  }

  public updateEula(isEulaAccepted: boolean, companyList?: any) {
    this.eluaPromise.next(isEulaAccepted);
    this.companyListPromise.next(companyList);
  }

  public cancelPendingRequests() {
    this.pendingHTTPRequests$.next();
  }

  public onCancelPendingRequests() {
    return this.pendingHTTPRequests$.asObservable();
  }

  public deleteUser() {}
}

